/**
 * Компонент успешной оплаты заказа.
 *
 * Для VK Mini App success-страница ждёт подтверждение оплаты
 * и оставляет пользователю ручной возврат обратно в мини-приложение.
 */

import {
  Icon24CalendarOutline,
  Icon24ClockOutline,
  Icon24UsersOutline,
  Icon28MoneyCircleOutline,
  Icon28ShoppingCartOutline,
  Icon56CheckCircleOutline,
} from '@vkontakte/icons';
import {
  Button,
  ButtonGroup,
  Card,
  Div,
  FormStatus,
  Panel,
  PanelHeader,
  Placeholder,
  Spacing,
  Spinner,
  Text,
  Title,
} from '@vkontakte/vkui';
import { useEffect, useRef, useState } from 'react';
import { buildPaymentResultApiUrl } from '../utils/apiBase';
import { reachOrderFlowGoal } from '../utils/metrika';
import { CoffeeTheme } from './CoffeeTheme';

const VK_POLL_INTERVAL_MS = 5000;
const ORDER_CLIENT_BASE_URL = import.meta.env.VITE_ORDER_CLIENT_URL || 'https://client.ultracafe.ru';

const MONTH_NAMES_GEN = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

const STATUS_LABELS = {
  pending_payment: 'Ожидает оплаты',
  pending_confirmation: 'На подтверждении',
  confirmed: 'Подтверждён',
  in_progress: 'Готовится',
  ready: 'Готов',
  completed: 'Выполнен',
};

function buildClientOrderUrl(orderUid) {
  if (!orderUid) {
    return null;
  }

  return `${ORDER_CLIENT_BASE_URL.replace(/\/$/, '')}/client/orders/${encodeURIComponent(orderUid)}`;
}

function getOrderTypeLabel(orderType) {
  if (orderType === 'purchase') {
    return 'Покупка';
  }

  if (orderType === 'booking_with_prepayment' || orderType === 'booking_no_prepayment') {
    return 'Бронирование';
  }

  return 'Заказ';
}

function formatCurrency(amount) {
  const normalizedAmount = Number(amount || 0);
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
  }).format(normalizedAmount);
}

function formatDateLabel(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);

      if (month >= 1 && month <= 12) {
        return `${day} ${MONTH_NAMES_GEN[month - 1]} ${year}`;
      }
    }
  }

  return value;
}

function formatTimeLabel(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{2}):(\d{2})/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
  }

  return value;
}

function resolveItemName(item) {
  return item.product_name || item.product?.name || `Товар #${item.product_id}`;
}

function resolveItemTotal(item) {
  const quantity = Number(item.quantity || 0);
  const unitPrice = Number(item.unit_price || 0);
  return quantity * unitPrice;
}

export function PurchaseSuccess({ paymentId }) {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const trackedSuccessRef = useRef(false);

  useEffect(() => {
    if (!paymentId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;

    const loadOrder = async () => {
      try {
        const response = await fetch(buildPaymentResultApiUrl(`/orders/api/payments/${paymentId}/order/`));
        if (!response.ok) {
          throw new Error('Не удалось загрузить данные заказа');
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }

        setOrderData(data);
        setError(null);
        setLoading(false);

        if (!trackedSuccessRef.current) {
          trackedSuccessRef.current = true;
          reachOrderFlowGoal(data.order_type, 'payment_success', {
            payment_id: paymentId,
            order_id: data.id,
            order_type: data.order_type,
            amount: data.paid_amount || data.total_amount,
            traffic_source: data.traffic_source || '',
            universal_app: data.universal_app || '',
          });
        }

        const shouldContinuePolling =
          data?.post_payment?.post_payment_mode === 'vk_wait_redirect' &&
          data?.post_payment?.is_ready !== true;

        if (shouldContinuePolling) {
          timeoutId = window.setTimeout(loadOrder, VK_POLL_INTERVAL_MS);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Error loading order:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadOrder();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [paymentId]);

  const handleGoToVkNow = () => {
    const nextUrl = orderData?.post_payment?.next_url;
    if (nextUrl) {
      window.location.href = nextUrl;
    }
  };

  const renderVkReturnNotice = () => {
    const postPayment = orderData?.post_payment;
    if (!postPayment || postPayment.post_payment_mode !== 'vk_wait_redirect') {
      return null;
    }

    if (!postPayment.is_ready) {
      return (
        <Div>
          <FormStatus title="Проверяем подтверждение оплаты">
            Как только backend получит подтверждение от IntellectMoney, можно будет вернуться в VK Mini App.
          </FormStatus>
        </Div>
      );
    }

    return (
      <Div>
        <FormStatus title="Можно вернуться в VK Mini App">
          Оплата подтверждена. Автоматического перехода не будет, но вы можете открыть мини-приложение вручную.
        </FormStatus>
      </Div>
    );
  };

  if (loading) {
    return (
      <CoffeeTheme>
        <Panel id="purchase_success" centered>
          <PanelHeader>Оплата</PanelHeader>
          <Placeholder icon={<Spinner size="l" />} title="Проверяем статус оплаты">
            Загружаем данные заказа и подтверждение платежа.
          </Placeholder>
        </Panel>
      </CoffeeTheme>
    );
  }

  if (error || !orderData) {
    return (
      <CoffeeTheme>
        <Panel id="purchase_success_fallback">
          <PanelHeader>Оплата</PanelHeader>
          <SuccessHeader subtitle="Ваш заказ оплачен и находится в обработке" />

          {paymentId && (
            <Div>
              <Card Component="section" mode="outline-tint">
                <PaymentDetailRow label="ID платежа" value={`#${paymentId}`} last />
              </Card>
            </Div>
          )}

          <Div>
            <FormStatus title="Что дальше">
              Мы подтвердим заказ в ближайшее время. Если вы платили из VK Mini App, можно вернуться в приложение вручную.
            </FormStatus>
          </Div>

          {error && (
            <Div>
              <FormStatus mode="error" title="Детали заказа не загрузились">
                {error}
              </FormStatus>
            </Div>
          )}

          <StickyActions>
            <Button Component="a" href="/" size="l" mode="primary" stretched>
              Вернуться на главную
            </Button>
          </StickyActions>
        </Panel>
      </CoffeeTheme>
    );
  }

  const bookingDetails = orderData.booking_details;
  const orderItems = Array.isArray(orderData.items) ? orderData.items : [];
  const isPurchase = orderData.order_type === 'purchase';
  const isBooking = orderData.order_type === 'booking_with_prepayment' || orderData.order_type === 'booking_no_prepayment';
  const orderTypeLabel = getOrderTypeLabel(orderData.order_type);
  const statusLabel = STATUS_LABELS[orderData.status] || orderData.status_display || 'Обрабатывается';
  const clientOrderUrl = buildClientOrderUrl(orderData.uid);
  const canReturnToVk = Boolean(orderData?.post_payment?.next_url);
  const bookingDate = formatDateLabel(bookingDetails?.booking_date || bookingDetails?.date);
  const bookingTime = formatTimeLabel(bookingDetails?.booking_time || bookingDetails?.time);
  const pickupDate = formatDateLabel(orderData.pickup_date);
  const pickupTime = formatTimeLabel(orderData.pickup_time);
  const fullDepositAmount = Number(bookingDetails?.full_deposit_amount || 0);
  const paidAmount = orderData.paid_amount || orderData.total_amount;

  return (
    <CoffeeTheme>
      <Panel id="purchase_success">
        {/* <PanelHeader>Оплата</PanelHeader> */}

        <div
          style={{
            display: 'flex',
            minHeight: 'calc(100dvh - 48px)',
            flexDirection: 'column',
            background: 'var(--vkui--color_background)',
          }}
        >
          <div style={{ flex: 1, overflow: 'auto' }}>
            <SuccessHeader subtitle="Ваш заказ оплачен и находится в обработке" />

            {/* {renderVkReturnNotice()} */}

            <Div>
              <Card Component="section" mode="outline-tint">
                <PaymentDetailRow label="Номер заказа" value={`#${orderData.id}`} />
                <PaymentDetailRow label="Статус" value={statusLabel} />
                <PaymentDetailRow label="Тип заказа" value={orderTypeLabel} />

                {orderData.company_name && (
                  <PaymentDetailRow
                    label={isPurchase ? 'Адрес самовывоза' : 'Заведение'}
                    value={orderData.company_name}
                  />
                )}

                {isPurchase && pickupDate && (
                  <PaymentDetailRow
                    icon={<Icon24CalendarOutline width={18} height={18} />}
                    label="Дата самовывоза"
                    value={pickupDate}
                  />
                )}
                {isPurchase && pickupTime && (
                  <PaymentDetailRow
                    icon={<Icon24ClockOutline width={18} height={18} />}
                    label="Время"
                    value={pickupTime}
                  />
                )}

                {isBooking && bookingDate && (
                  <PaymentDetailRow
                    icon={<Icon24CalendarOutline width={18} height={18} />}
                    label="Дата"
                    value={bookingDate}
                  />
                )}
                {isBooking && bookingTime && (
                  <PaymentDetailRow
                    icon={<Icon24ClockOutline width={18} height={18} />}
                    label="Время"
                    value={bookingTime}
                  />
                )}
                {isBooking && bookingDetails?.guests_count && (
                  <PaymentDetailRow
                    icon={<Icon24UsersOutline width={18} height={18} />}
                    label="Гостей"
                    value={`${bookingDetails.guests_count} чел.`}
                  />
                )}

                {orderItems.length > 0 && (
                  <div
                    style={{
                      padding: '14px 16px',
                      borderBottom: '1px solid var(--vkui--color_separator_primary)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        color: 'var(--vkui--color_text_secondary)',
                        fontSize: 13,
                        marginBottom: 10,
                      }}
                    >
                      <Icon28ShoppingCartOutline width={18} height={18} />
                      Товары в заказе
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {orderItems.map((item, index) => (
                        <div
                          key={item.id || `${item.product_id || item.product_name || 'item'}-${index}`}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 12,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <Text weight="2" style={{ color: 'var(--vkui--color_text_primary)' }}>
                              {resolveItemName(item)}
                            </Text>
                            <Text style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>
                              {item.quantity} шт. x {formatCurrency(item.unit_price)}
                            </Text>
                          </div>
                          <Text
                            weight="2"
                            style={{ whiteSpace: 'nowrap', color: 'var(--vkui--color_text_primary)' }}
                          >
                            {formatCurrency(resolveItemTotal(item))}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {orderData.order_type === 'booking_with_prepayment' && fullDepositAmount > 0 ? (
                  <>
                    <PaymentDetailRow
                      icon={<Icon28MoneyCircleOutline width={20} height={20} />}
                      label="Депозит"
                      value={formatCurrency(bookingDetails.full_deposit_amount)}
                    />
                    {Number(orderData.discount_amount) > 0 && (
                      <PaymentDetailRow label="Скидка" value={`-${formatCurrency(orderData.discount_amount)}`} />
                    )}
                    <PaymentDetailRow label="Оплачено" value={formatCurrency(paidAmount)} last />
                  </>
                ) : (
                  <PaymentDetailRow
                    icon={<Icon28MoneyCircleOutline width={20} height={20} />}
                    label="Итого оплачено"
                    value={formatCurrency(paidAmount)}
                    last
                  />
                )}
              </Card>
            </Div>

            <div style={{ height: 16 }} />
          </div>

          <StickyActions>
            <ButtonGroup mode="vertical" gap="s" stretched>
              {clientOrderUrl && (
                <Button Component="a" href={clientOrderUrl} size="l" mode="primary" stretched>
                  {isBooking ? 'Управлять записью' : 'Смотреть статус заказа'}
                </Button>
              )}
              {/* {canReturnToVk && (
                <Button type="button" onClick={handleGoToVkNow} size="l" mode="secondary" stretched>
                  Вернуться в VK Mini App
                </Button>
              )} */}
            </ButtonGroup>
          </StickyActions>
        </div>
      </Panel>
    </CoffeeTheme>
  );
}

function SuccessHeader({ subtitle }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '28px 24px 16px',
        textAlign: 'center',
      }}
    >
      <Icon56CheckCircleOutline width={64} height={64} fill="var(--vkui--color_icon_positive)" />
      <Spacing size="2xl" />
      <Title level="1" style={{ color: 'var(--vkui--color_text_primary)' }}>
        Оплата прошла успешно
      </Title>
      <Spacing size="m" />
      <Text style={{ color: 'var(--vkui--color_text_secondary)', maxWidth: 300 }}>
        {subtitle}
      </Text>
    </div>
  );
}

function PaymentDetailRow({ icon, label, value, last = false }) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        borderBottom: last ? 'none' : '1px solid var(--vkui--color_separator_primary)',
      }}
    >
      {icon && <div style={{ flexShrink: 0, marginTop: 2 }}>{icon}</div>}
      <div style={{ minWidth: 0 }}>
        <div style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>{label}</div>
        <div
          style={{
            marginTop: 2,
            color: 'var(--vkui--color_text_primary)',
            fontSize: 15,
            fontWeight: 500,
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function StickyActions({ children }) {
  return (
    <div
      style={{
        flexShrink: 0,
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        padding: '12px 16px 24px',
        paddingLeft: 'max(16px, env(safe-area-inset-left, 0px))',
        paddingRight: 'max(16px, env(safe-area-inset-right, 0px))',
        paddingBottom: 'max(24px, env(safe-area-inset-bottom, 0px))',
        background: 'var(--vkui--color_background)',
        borderTop: '1px solid var(--vkui--color_separator_primary)',
      }}
    >
      {children}
    </div>
  );
}
